/*global chrome*/
import React, { useEffect, useRef, useState, useMemo } from 'react';
import {
  Button,
  Link,
  Flex,
  SimpleSelect,
  IconRefreshSolid,
  IconButton,
  View,
  Alert,
  FlexItem,
} from '@smarterservices/smarter-ui';
import StepInstructions from '../Common/StepInstructions';
import { sendMessageToExtension, parseMediaDeviceError, getOperatingSystem } from '../../helpers/helpers';
import DualPane from '../DualPane';
import { usePaneViewer } from '../PaneViewer';
import Loader from '../Common/Loader';
import PageError from '../PageError';
import { useAppStore } from '../../stores/app';
import { useLogger } from '@ui/shared/components/Logger';

/**
 * StandardWebcamShare component handles webcam selection, initialization, and sharing
 * @param {Object} props - Component props
 * @param {Object} props.stepData - Configuration data for the current step
 * @param {Function} props.setIsTaskComplete - Function to mark the task as complete
 * @param {Function} props.setStepMetadata - Function to update step metadata
 * @returns {React.ReactElement} The rendered component
 */
const StandardWebcamShare = ({ stepData, setIsTaskComplete, setStepMetadata }) => {


  const CAMERA_STREAM_TIMEOUT = 5000; // 5 second timeout for video stream initialization
  
  const [isSharing, setIsSharing] = useState(false);
  const [videoInputs, setVideoInputs] = useState([]);
  const [isLoadingVideoDevices, setIsLoadingVideoDevices] = useState(true);
  const [selectedVideoLabel, setSelectedVideoLabel] = useState('');
  const [selectedVideoId, setSelectedVideoId] = useState('');
  const [videoStream, setVideoStream] = useState(null);
  const [isVideoStreamLoading, setIsVideoStreamLoading] = useState(true);
  const [videoStreamError, setVideoStreamError] = useState('');
  const [isShowingOptions, setIsShowingOptions] = useState(false);
  const [videoStreamErrorCode, setVideoStreamErrorCode] = useState('');
  const [videoStreamErrorReplacements, setVideoStreamErrorReplacements] = useState({});
  const videoRef = useRef(null);
  const { onNext } = usePaneViewer();
  const logger = useLogger();

  /**
   * Initialize component by fetching available video devices
   */
  useEffect(() => {
    getVideoDevices();
  }, []);

  /**
   * Handle video stream initialization with timeout and error handling
   * Sets up event listeners for video element and manages loading states
   */
  useEffect(() => {
    let playTimeout;
    if(videoRef.current){

        playTimeout = setTimeout(() => {
            setIsVideoStreamLoading(false);
            setVideoStreamError('Camera stream could not be obtained');           
        }, CAMERA_STREAM_TIMEOUT);

        videoRef.current.onloadedmetadata = () => {
            videoRef.current.play()
            .then(() => {
                clearTimeout(playTimeout); // Clear the timeout if video plays successfully
                setIsVideoStreamLoading(false);
            })
            .catch((error) => {
                setVideoStreamError('Error loading camera stream.'); 
                clearTimeout(playTimeout);
                setIsVideoStreamLoading(false);
            });
        };

        // Handle case where video errors out
        videoRef.current.onerror = () => {
            setVideoStreamError('Error loading camera stream.'); 
            clearTimeout(playTimeout);
            setIsVideoStreamLoading(false);
        };
    }
    return () => {
      if (playTimeout) {
        clearTimeout(playTimeout);
        setVideoStreamError('');        
      }
    };
  }, [videoRef?.current?.srcObject]);


  /**
   * Fetches available video devices and initializes the webcam stream
   * Handles cases where webcam is already shared, previously selected, or default
   */
  const getVideoDevices = async () => { 
    try {
      setIsLoadingVideoDevices(true);
      /**
       * Check if webcam is already shared
       */
      const isAlreadyPublished = await sendMessageToExtension({
        message: 'sp_background__isTrackPublished',
        trackName: 'webcam',
      });
      if (isAlreadyPublished) {
        setIsSharing(true);
        setIsTaskComplete(true);
        // now auto advance to next step since webcam has been shared...
        onNext();
        return;
      }
      /**
       * Request camera permissions first to ensure we get the permission dialog
       * This ensures enumerate devices will return labeled devices
       */
      const defaultStream = await navigator.mediaDevices.getUserMedia({
        video: true,
      });

      /**
       * Get all available video input devices
       */
      const devices = await navigator.mediaDevices.enumerateDevices();
      const cameras = devices.filter((device) => device.kind === 'videoinput');
      setVideoInputs(cameras);
      setIsLoadingVideoDevices(false);

      /**
       * Check for previously selected webcam in local storage
       */
      const previouslySelectedWebcamLabel = localStorage.getItem(
        'spSelectedWebcamLabel'
      );

      if (previouslySelectedWebcamLabel) {
        /**
         * Check if previously selected webcam is available
         */
        const previouslySelectedCamera = cameras.find(
          (camera) => camera.label === previouslySelectedWebcamLabel
        );

        if (previouslySelectedCamera) {
          // Stop default stream as we'll use the selected one
          defaultStream.getTracks().forEach((track) => track.stop());
          // Initialize stream with previously selected webcam
          const stream = await navigator.mediaDevices.getUserMedia({
            video: {
              deviceId: { exact: previouslySelectedCamera.deviceId },
            },
            audio: false,
          });

          setSelectedVideoLabel(previouslySelectedCamera.label);
          setVideoStream(stream);

          if (videoRef.current) {
            videoRef.current.srcObject = stream;

            videoRef.current.onloadedmetadata = () => {
                videoRef.current.play().then(() => {
                  setIsVideoStreamLoading(false);
                });
              };
          }
          return;
        }
      }

      /**
       * No previously selected device was found, use the machine default
       * Get the default video device ID from the active track
       */
      const defaultDeviceId = defaultStream
        .getVideoTracks()[0]
        .getSettings().deviceId;
      if (defaultDeviceId) {
        const defaultCamera = cameras.find(
          (cam) => cam.deviceId === defaultDeviceId
        );
        setSelectedVideoLabel(
          defaultCamera ? defaultCamera.label : 'Unknown Camera'
        );
        setVideoStream(defaultStream);
        if (videoRef.current) {
          videoRef.current.srcObject = defaultStream;
        }
      } else {
        // The default device was not found, so stop the stream
        defaultStream.getTracks().forEach((track) => track.stop());
      }
    } catch (error) {
      const mediaError = parseMediaDeviceError(error);
      // lets add logging here so we can catch any errors for support and debugging.
      setVideoStreamErrorCode(mediaError.code);
      // now add the query params back to the URL, this way when the browser re-opens it can open to this step...
      useAppStore.getState().restoreLaunchParams();
      logger.logError("SessionUI", 'Error fetching video devices:', mediaError);
      console.log('Error fetching video devices:', mediaError);
    }
  };

  /**
   * Handles camera selection change from the dropdown
   * Stops previous stream and initializes a new one with the selected device
   * 
   * @param {Event} event - The change event
   * @param {Object} selected - The selected option data
   * @param {string} selected.id - The device ID of the selected camera
   * @param {string} selected.value - The label of the selected camera
   */
  const handleSelectChange = async (event, { id, value }) => {
    const selectedDevice = videoInputs.find((device) => device.deviceId === id);
    setSelectedVideoLabel(value);
    setSelectedVideoId(id);
    

    if (videoStream) {
      // Stop the previous stream
      videoStream.getTracks().forEach((track) => track.stop());
    }

    try {
      const newStream = await navigator.mediaDevices.getUserMedia({
        video: {
          deviceId: { exact: id },
        },
        audio: false,
      });
      setVideoStream(newStream);
      setIsVideoStreamLoading(true);
      
      if (videoRef.current) {
        // Set the new stream as the video source
         videoRef.current.srcObject = newStream;   
      }
    } catch (error) {
      console.log('Error getting video stream:', error);
      setVideoStreamError('Error loading camera stream.'); 
    }
    setIsShowingOptions(false);
  };

  /**
   * Handles sharing the selected video stream with the proctor
   * Stores the selected camera in local storage and publishes the track
   */
  const handleShareVideo = async () => {
    try {
      if (
        !selectedVideoLabel ||
        (selectedVideoLabel && selectedVideoLabel === '')
      ) {
        // No valid video input selected
        return;
      }

      /**
       * Store selected webcam in local storage and step metadata
       */
      setStepMetadata({ selectedWebcam: selectedVideoLabel });
      localStorage.setItem('spSelectedWebcamLabel', selectedVideoLabel);

      /**
       * Share video through the extension
       */
      const response = await sendMessageToExtension({
        message: 'sp_background__shareVideo',
        deviceLabel: selectedVideoLabel,
        trackName: 'webcam',
      });

      if (response === null) {
        // No listener is set up on the extension
        window.location.reload();
        return;
      }

      setIsSharing(response);

      if (response) {
        // Video was successfully shared
        setIsTaskComplete(true);
        // now auto advance to next step since webcam has been shared...
        onNext();
      }
    } catch (error) {
      console.error('Error while sharing video:', { error });
      window.location.reload();
    }
  };

  // If there is an error, surface the proper error screen...
  if (videoStreamErrorCode) {
    const errorContent = stepData?.mediaErrors?.[videoStreamErrorCode];
    // get the OS of the user...
    const os = getOperatingSystem(window).toLowerCase();

    let errorText = "";
    // Log that the OS was not found in the error content...
    if(!errorContent?.[os]){
      logger.logError("SessionUI", 'Error fetching page error content for OS (webcam):', os, null, {appVersion: window.navigator.appVersion});
      errorText = "There was an error accessing your camera. Please check your settings, if the problem persists reach out to support.";
    }else{
      errorText = errorContent?.[os];
    }

    return (
      <PageError
        errorText={errorText}
        showRefreshButton={errorContent?.showRefreshButton}
        refreshButtonText={errorContent?.refreshButtonText}
        refreshIntroText={errorContent?.refreshIntroText}
        errorCode={videoStreamErrorCode}        
        maxWidth="600px"
      />
    );
  }

  /**
   * Render the webcam sharing interface with device selection, video preview, and controls
   */
  return (
    <DualPane
      leftPane={
        <StepInstructions 
          title="Instructions" 
          instructions={stepData?.body} 
        />
        }
      rightPane={
        
        <Flex direction="column" alignItems="center" width="100%">
          <Flex.Item>
            <Flex
              direction="row"
              justifyItems="center"
              alignItems="center"
              margin="0 0 medium 0"
              width="100%"
              
            >
              <View as="div" width="400px">
                {isLoadingVideoDevices && <Loader textSize="medium" spinnerSize="x-small" spinnerMargin="0 small 0 0" textAs="span" text="Loading webcams..." />}
                {!isLoadingVideoDevices && <SimpleSelect
                  renderLabel=""
                  assistiveText="Select your webcam from the dropdown using the arrow keys"
                  value={selectedVideoLabel}
                  size="medium"
                  width="400px"
                  onChange={handleSelectChange}
                >
                  {videoInputs.length > 0
                  ? (videoInputs.map((device) => (
                      <SimpleSelect.Option
                        key={device.deviceId}
                        id={device.deviceId}
                        value={device.label}
                      >
                        {device.label ||
                          `Camera ${videoInputs.indexOf(device) + 1}`}
                      </SimpleSelect.Option>
                    ))
                  ): (
                    <SimpleSelect.Option id="no-cams" value="no-cams">
                      No webcams available
                    </SimpleSelect.Option>
                  )}
              </SimpleSelect>}
              </View>
              <IconButton
                color="primary"
                margin="0 0 0 small"
                onClick={getVideoDevices}
                screenReaderLabel="Refresh"
              >
                <IconRefreshSolid />
              </IconButton>
            </Flex>
          </Flex.Item>
          <Flex.Item as="div" alignItems="center" textAlign="center">
            {(!isLoadingVideoDevices && isVideoStreamLoading && !videoStreamError) && <Loader text="Initializing stream..." />}
            {(!isLoadingVideoDevices && !isVideoStreamLoading && videoStreamError) && 
              <Alert
                variant="error"
                variantScreenReaderLabel="Error"
              >
                {videoStreamError}
              </Alert>}
          
            <View as="div" textAlign="center"
              style={{
                  display: (isVideoStreamLoading || videoStreamError) ? 'none' : 'block'
              }}
              >
                  <div
                  style={{
                      width: '100%',
                      position: 'relative',
                      overflow: 'hidden',
                  }}
                  >
                  <video
                      ref={videoRef}
                      autoPlay
                      playsInline
                      style={{
                          width: '98%',
                          maxHeight: '40vh',
                          objectFit: 'contain',
                          borderRadius: '4px',
                          border: `1px solid #ccc`,
                          margin: '0',
                          boxShadow: 'rgba(0, 0, 0, 0.1) 0px 0.1875rem 0.375rem, rgba(0, 0, 0, 0.16) 0px 0.1875rem 0.375rem',
                          transform: 'scaleX(-1)' // Mirror the video for selfie view
                      }}
                  />
                  </div>
            </View>
          
          {(!isLoadingVideoDevices && !isVideoStreamLoading && !videoStreamError) &&
              <Button
              color="primary"
              size="medium"
              onClick={handleShareVideo}
              interaction={
                  isSharing || !selectedVideoLabel ? 'disabled' : 'enabled'
              }
              margin="medium 0 0 0"
              >
              {stepData?.button}
              </Button>
              }
          </Flex.Item>
        </Flex>
      
      }
    />
  );
};

export default StandardWebcamShare;
